import React, { useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Typography,
  Popconfirm,
  message,
  Switch,
  Checkbox,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { getAllHubAppProfiles } from '../services/hubAppProfileService';

const { Title } = Typography;

const Users = () => {
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const { data: appProfiles = [] } = useQuery(['hub-app-profiles'], getAllHubAppProfiles, {
    staleTime: 5 * 60 * 1000,
  });
  const analyticsPermissionOptions = appProfiles
    .filter((app) => app.analytics?.enabled)
    .map((app) => ({
      label: `${app.analytics?.required_permission || `read_${app.id}_analytics`}`,
      value: app.analytics?.required_permission || `read_${app.id}_analytics`,
    }));
  const permissionLabelMap = analyticsPermissionOptions.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  // Fetch users
  const { data, isLoading, refetch } = useQuery(
    ['users', currentPage, searchText, roleFilter],
    async () => {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        search: searchText,
        role: roleFilter,
      });
      const response = await apiClient.get(`/users?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  // Create mutation
  const createMutation = useMutation(
    async (userData) => {
      const response = await apiClient.post('/users', userData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User created successfully');
        queryClient.invalidateQueries('users');
        setCreateModalVisible(false);
        createForm.resetFields();
      },
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await apiClient.put(`/users/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User updated successfully');
        queryClient.invalidateQueries('users');
        setEditModalVisible(false);
        editForm.resetFields();
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/users/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User deleted successfully');
        queryClient.invalidateQueries('users');
      },
    }
  );

  const getRoleColor = (role) => {
    return role === 'admin' ? 'red' : 'blue';
  };

  const handleCreate = (values) => {
    createMutation.mutate({
      ...values,
      permissions: values.permissions || [],
    });
  };

  const handleEdit = (record) => {
    setSelectedUser(record);
    editForm.setFieldsValue({
      username: record.username,
      email: record.email,
      role: record.role,
      isActive: record.isActive,
      permissions: record.permissions || [],
    });
    setEditModalVisible(true);
  };

  const handleUpdate = (values) => {
    updateMutation.mutate({
      id: selectedUser._id,
      data: values,
    });
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    refetch();
  };

  const handleReset = () => {
    setSearchText('');
    setRoleFilter('');
    setCurrentPage(1);
  };

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <Space>
          <UserOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)} className="role-tag">
          {role.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions = []) => {
        if (!permissions.length) return '-';
        return permissions.map((permission) => permissionLabelMap[permission] || permission).join(', ');
      },
      responsive: ['lg'],
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => createdBy?.username || '-',
      responsive: ['md'],
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isCurrentUser = record._id === currentUser._id;
        return (
          <Space>
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
              disabled={isCurrentUser}
            >
              <Button type="text" danger icon={<DeleteOutlined />} title="Delete" disabled={isCurrentUser} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2} className="page-title">
          User Management
        </Title>
      </div>

      <Card>
        <div className="table-actions">
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Select
                placeholder="Role"
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="admin">Admin</Select.Option>
                <Select.Option value="staff">Staff</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Space>
                <Button type="primary" onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleReset}>Reset</Button>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()} title="Refresh" />
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                Add User
              </Button>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={data?.users}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={createMutation.isLoading}
      >
        <Form form={createForm} layout="vertical" initialValues={{ permissions: [] }} onFinish={handleCreate}>
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please input username!' },
              { min: 3, message: 'Username must be at least 3 characters!' },
              { max: 30, message: 'Username cannot exceed 30 characters!' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please enter valid email!' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input password!' },
              { min: 6, message: 'Password must be at least 6 characters!' },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select role!' }]}>
            <Select>
              <Select.Option value="staff">Staff</Select.Option>
              <Select.Option value="admin">Admin</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="permissions"
            label="Permissions"
            extra="Grant which app analytics this account can view. Permission code is shown in brackets."
          >
            <Checkbox.Group options={analyticsPermissionOptions} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={updateMutation.isLoading}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please input username!' },
              { min: 3, message: 'Username must be at least 3 characters!' },
              { max: 30, message: 'Username cannot exceed 30 characters!' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please enter valid email!' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please select role!' }]}>
            <Select>
              <Select.Option value="staff">Staff</Select.Option>
              <Select.Option value="admin">Admin</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="permissions" label="Permissions">
            <Checkbox.Group options={analyticsPermissionOptions} />
          </Form.Item>

          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              disabled={selectedUser?._id === currentUser._id}
            />
          </Form.Item>

          {selectedUser?._id === currentUser._id && (
            <div style={{ color: '#999', fontSize: '12px', marginTop: '-16px' }}>
              You cannot deactivate your own account
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Users;

